import { useMsal } from '@azure/msal-react';
import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DownloadSVG from '../../assets/images/icons/DownloadSVG';
import { exportToPDF } from '../../utils/agGridPdfExporter';
import { FAQ_INSIGHT_CATEGORY_SEARCH_API_URL } from '../../utils/apiUrls';
import { getAccessToken } from '../../utils/token-service';
import FaqCategoryDropdown from './faq-category-dropdown';

interface ApiResponse {
	id: number;
	faq: string;
	answer: string;
	rank: number;
	likes: number;
	dislikes: number;
}
interface CellRendererParams {
	value: number;
}
const AdminToolInfo: React.FC = () => {
	const { instance } = useMsal();
	const gridRef = useRef<AgGridReact>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [activeCategoryId, setActiveCategoryId] = useState<number | null>(2);
	const [faqData, setFaqData] = useState<ApiResponse[]>([]);

	const handleStepNameClick = (id: number) => {
		setActiveCategoryId(id);
	};
	// fetch faq data for selected category.
	const fetchData = async (categoryId: number) => {
		setLoading(true);
		try {
			let accessToken = await getAccessToken();
			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: FAQ_INSIGHT_CATEGORY_SEARCH_API_URL + `/${categoryId}`,
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			};
			const response = await axios.request(config);
			setFaqData(response.data);
		} catch (error) {
			console.log('Error calling API:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (activeCategoryId !== null) {
			fetchData(activeCategoryId);
		}
	}, [activeCategoryId]);

	const ActionCellRenderer = (params: any) => {
		const [isEditing, setIsEditing] = useState(false);
		const editingCells = params.api.getEditingCells();
		const isCurrentRowEditing = editingCells.some((cell: any) => cell.rowIndex === params.node.rowIndex);
		const handleEditClick = () => {
			setIsEditing(true);
			if (!isCurrentRowEditing) {
				params.api.startEditingCell({
					rowIndex: params.node.rowIndex,
					colKey: 'faq',
				});
				params.api.startEditingCell({
					rowIndex: params.node.rowIndex,
					colKey: 'answer',
				});
			}
		};
		const handleUpdateClick = async () => {
			const accessToken = await getAccessToken();
			setIsEditing(false);
			params.api.stopEditing(false);
			try {
				const accounts = instance.getAllAccounts();
				const loggedInUserName = accounts && accounts.length > 0 ? accounts[0].name : '';
				const config = {
					method: 'put',
					url: FAQ_INSIGHT_CATEGORY_SEARCH_API_URL + `/${params.data.id}`,
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
					},
					data: {
						faq_category_id: activeCategoryId,
						faq: params.data.faq,
						answer: params.data.answer,
						rank: params.data.rank,
						updated_by: loggedInUserName,
					},
				};

				const response = await axios.request(config);

				// Fetch updated data
				if (activeCategoryId !== null) {
					fetchData(activeCategoryId);
				}
			} catch (error) {
				console.log('Error updating FAQ:', error);
			}
		};

		const handleCancelClick = () => {
			setIsEditing(false);
			params.api.stopEditing(true);
		};

		const handleDelete = async (data: any) => {
			try {
				const accessToken = await getAccessToken();
				const config = {
					method: 'delete',
					url: `${FAQ_INSIGHT_CATEGORY_SEARCH_API_URL}/${data.id}`,
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				};

				const response = await axios.request(config);

				// Fetch updated data after deletion
				if (activeCategoryId !== null) {
					fetchData(activeCategoryId);
				}
			} catch (error) {
				console.log('Error deleting FAQ:', error);
			}
		};

		return (
			<div className="text-[12px] font-medium mt-1">
				{isEditing ? (
					<div className="flex items-center gap-2">
						<button
							className="flex items-center justify-between px-2 py-1 leading-4 shadow-sm rounded text-white bg-green-500"
							onClick={handleUpdateClick}
						>
							Update
						</button>
						<button
							className="flex items-center justify-between px-2 py-1 leading-4 shadow-sm rounded bg-gray-200"
							onClick={handleCancelClick}
						>
							Cancel
						</button>
					</div>
				) : (
					<div className="flex items-center gap-2 text-white">
						<button
							className="flex items-center justify-between px-2 py-1 leading-4 bg-sky-600 shadow-sm rounded"
							onClick={handleEditClick}
						>
							Edit
						</button>
						<button
							className="flex items-center justify-between px-2 py-1 leading-4 bg-pink-600 shadow-sm rounded"
							onClick={() => handleDelete(params.data)}
						>
							Delete
						</button>
					</div>
				)}
			</div>
		);
	};

	const LikeCellRenderer: React.FC<CellRendererParams> = (params) => (
		<div className="flex items-center mt-1.5">
			<HandThumbUpIcon className="h-5 w-5 text-green-500" />
			<span className="ml-1">{params.value}</span>
		</div>
	);

	const DislikeCellRenderer: React.FC<CellRendererParams> = (params) => (
		<div className="flex items-center mt-1.5">
			<HandThumbDownIcon className="h-5 w-5 text-red-500" />
			<span className="ml-1">{params.value}</span>
		</div>
	);

	const colDefs = [
		{ field: 'rank', width: 90 },
		{ field: 'faq', headerName: 'Question', width: 280, editable: true },
		{ field: 'answer', headerName: 'Answer', flex: 1, editable: true },
		{
			field: 'likes',
			headerName: 'Likes',
			width: 90,
			cellRenderer: LikeCellRenderer,
		},
		{ field: 'dislikes', headerName: 'Dislikes', width: 100, cellRenderer: DislikeCellRenderer },
		{
			field: 'Action',
			width: 130,
			editable: false,
			cellRenderer: ActionCellRenderer,
		},
	];

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapText: true,
			autoHeight: true,
			cellStyle: { lineHeight: '20px', padding: '8px 14px', wordWrap: 'break-word' },
		}),
		[]
	);

	const onBtnExport = useCallback(() => {
		gridRef.current?.api.exportDataAsCsv();
	}, []);

	const handleExportPDF = () => {
		let filename = 'exported-data.pdf';
		if (activeCategoryId) {
			const activeCategoryIdNumber = Number(activeCategoryId);
			// Define the map with an index signature to allow numeric indices.
			const idToStepNameMap: { [key: number]: string } = {
				2: 'Address Search',
				3: 'Customer Info',
				4: 'Internet Plans',
				5: 'Phone Plans',
				6: 'Phone Configuration',
				7: 'Schedule Install',
				8: 'Billing Information',
				9: 'Review and Verification',
			};
			const stepName = idToStepNameMap[activeCategoryIdNumber];
			if (stepName) {
				filename = `${stepName.replace(/\s+/g, '-')}-data.pdf`;
			} else {
				console.error('Invalid category ID');
				return;
			}
		}

		if (gridRef.current?.api) {
			exportToPDF(gridRef.current.api, filename);
		} else {
			console.error('Failed to access grid API');
		}
	};

	return (
		<div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
			<h1 className="text-lg font-semibold leading-6 mb-4 text-gray-900">Admin Tool</h1>
			<div className="flex justify-between items-end">
				<div className="w-64">
					<FaqCategoryDropdown onStepNameClick={handleStepNameClick} />
				</div>
				<div className="flex gap-2">
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={onBtnExport}
					>
						Export CSV <DownloadSVG />
					</button>
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={handleExportPDF}
					>
						Export PDF <DownloadSVG />
					</button>
				</div>
			</div>
			<div className="flex mt-2 pt-2 border-t">
				<div className="flex-1">
					{loading ? (
						<div className="text-sm">Loading...</div>
					) : (
						<div className="ag-theme-alpine" style={{ height: '70vh' }}>
							<AgGridReact
								ref={gridRef}
								suppressExcelExport={true}
								pagination={true}
								paginationPageSize={20}
								defaultColDef={defaultColDef}
								rowData={faqData}
								columnDefs={colDefs}
								rowSelection={'multiple'}
								editType={'fullRow'}
								pivotPanelShow={'always'}
								reactiveCustomComponents
								enableCellEditingOnBackspace={true}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
export default AdminToolInfo;
